import React, { useState } from 'react';
import { X, Trophy, Plus, Hash, Clock, Users, Copy, Check, Loader2, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useTournament, type Tournament } from '../hooks/useTournament';
import { User } from 'firebase/auth';
import { cn } from '../lib/utils';
import { useSettings } from './SettingsContext';

interface PrivateTournamentModalProps {
  user: User | null;
  onClose: () => void;
}

type TabType = 'my' | 'create' | 'join';

export default function PrivateTournamentModal({ user, onClose }: PrivateTournamentModalProps) {
  const { playSfx, triggerHaptic } = useSettings();
  const { privateTournaments, createPrivateTournament, joinPrivateTournament } = useTournament(user);

  const [activeTab, setActiveTab] = useState<TabType>('my');
  const [createName, setCreateName] = useState('');
  const [createDuration, setCreateDuration] = useState(7);
  const [joinCode, setJoinCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const handleTabChange = (tab: TabType) => {
    playSfx('click');
    triggerHaptic('light');
    setActiveTab(tab);
    setError(null);
    setSuccess(null);
  };

  const handleCreate = async () => {
    if (!createName.trim()) {
      setError('Inserisci un nome per il torneo');
      return;
    }
    setCreating(true);
    setError(null);
    const id = await createPrivateTournament(createName.trim(), createDuration);
    if (id) {
      setSuccess('Torneo creato con successo!');
      setCreateName('');
      setActiveTab('my');
    } else {
      setError('Errore nella creazione del torneo');
    }
    setCreating(false);
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) {
      setError('Inserisci il codice di invito');
      return;
    }
    setJoining(true);
    setError(null);
    const ok = await joinPrivateTournament(joinCode.trim());
    if (ok) {
      setSuccess('Ti sei unito al torneo!');
      setJoinCode('');
      setActiveTab('my');
    } else {
      setError('Codice non valido o torneo non trovato');
    }
    setJoining(false);
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const getStatusBadge = (t: Tournament) => {
    const now = Date.now();
    if (t.status === 'finished' || now > t.endsAt) {
      return <span className="text-[8px] bg-white/10 text-white/50 px-1.5 py-0.5 rounded font-bold uppercase">Finito</span>;
    }
    if (t.status === 'active' || (now >= t.startsAt && now <= t.endsAt)) {
      return <span className="text-[8px] bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded font-bold uppercase">Attivo</span>;
    }
    return <span className="text-[8px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded font-bold uppercase">In attesa</span>;
  };

  const formatDuration = (days: number) => {
    if (days === 1) return '1 giorno';
    if (days === 7) return '7 giorni';
    if (days === 14) return '14 giorni';
    return `${days} giorni`;
  };

  const formatEndsAt = (ts: number) => {
    const diff = ts - Date.now();
    if (diff <= 0) return 'Terminato';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}g ${hours}h`;
    return `${hours}h`;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.95, y: 15, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 15, opacity: 0 }}
          className="w-full max-w-lg glass-panel rounded-3xl relative border border-white/10 flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-hidden shadow-2xl my-4 sm:my-6"
        >
          {/* Header */}
          <div className="p-5 sm:p-6 pb-3 border-b border-white/5 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-2.5">
              <Trophy className="w-6 h-6 text-yellow-400" />
              <h2 className="text-xl sm:text-2xl font-black uppercase text-yellow-400 tracking-wider" style={{ fontFamily: '"Playfair Display", serif' }}>
                Tornei Privati
              </h2>
            </div>
            <button
              onClick={() => { playSfx('click'); triggerHaptic('light'); onClose(); }}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Tab Selector */}
          <div className="px-5 sm:px-6 pt-3 pb-2 shrink-0">
            <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
              <button
                onClick={() => handleTabChange('my')}
                className={cn(
                  "flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all",
                  activeTab === 'my'
                    ? "bg-yellow-500 text-black shadow-md font-black"
                    : "text-white/60 hover:text-white/90"
                )}
              >
                I Miei
              </button>
              <button
                onClick={() => handleTabChange('create')}
                className={cn(
                  "flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all",
                  activeTab === 'create'
                    ? "bg-yellow-500 text-black shadow-md font-black"
                    : "text-white/60 hover:text-white/90"
                )}
              >
                + Crea
              </button>
              <button
                onClick={() => handleTabChange('join')}
                className={cn(
                  "flex-1 py-2 text-center text-xs font-bold rounded-lg transition-all",
                  activeTab === 'join'
                    ? "bg-yellow-500 text-black shadow-md font-black"
                    : "text-white/60 hover:text-white/90"
                )}
              >
                Unisciti
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-4 scrollbar-thin">
            {/* MY TOURNAMENTS TAB */}
            {activeTab === 'my' && (
              <div className="space-y-3">
                {privateTournaments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Trophy className="w-10 h-10 text-white/20 mb-3" />
                    <p className="text-white/50 text-sm">Nessun torneo privato</p>
                    <p className="text-white/30 text-xs mt-1">Crea un nuovo torneo o unisciti con un codice</p>
                  </div>
                ) : (
                  privateTournaments.map(t => (
                    <div key={t.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-white">{t.name}</span>
                          {getStatusBadge(t)}
                        </div>
                        <button
                          onClick={() => copyCode(t.inviteCode)}
                          className="flex items-center gap-1 text-[10px] text-white/40 hover:text-white/70 transition-colors"
                          title="Copia codice"
                        >
                          {copiedCode === t.inviteCode ? (
                            <Check className="w-3 h-3 text-green-400" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                          <span className="font-mono">{t.inviteCode}</span>
                        </button>
                      </div>
                      <div className="flex items-center gap-4 text-[10px] text-white/40">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{formatDuration(t.durationDays)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          <span>{Object.keys(t.players).length} giocatori</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Trophy className="w-3 h-3" />
                          <span>Scade tra {formatEndsAt(t.endsAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* CREATE TAB */}
            {activeTab === 'create' && (
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-white/70 uppercase tracking-wider">Nome Torneo</label>
                  <input
                    type="text"
                    value={createName}
                    onChange={e => setCreateName(e.target.value.slice(0, 30))}
                    placeholder="Es. Sfide tra amici"
                    className="w-full px-4 py-3 bg-black/40 border border-white/10 hover:border-white/20 focus:border-yellow-500/50 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none transition-all"
                    maxLength={30}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-white/70 uppercase tracking-wider">Durata</label>
                  <div className="flex gap-2">
                    {[1, 7, 14].map(d => (
                      <button
                        key={d}
                        onClick={() => { playSfx('click'); triggerHaptic('light'); setCreateDuration(d); }}
                        className={cn(
                          "flex-1 py-3 rounded-xl text-xs font-bold transition-all border",
                          createDuration === d
                            ? "bg-yellow-500 text-black border-yellow-500 shadow-md"
                            : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                        )}
                      >
                        {formatDuration(d)}
                      </button>
                    ))}
                  </div>
                </div>
                {error && <p className="text-red-400 text-xs">{error}</p>}
                {success && <p className="text-green-400 text-xs">{success}</p>}
                <button
                  onClick={() => { playSfx('click'); triggerHaptic('medium'); handleCreate(); }}
                  disabled={creating}
                  className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:bg-white/10 text-black font-black text-sm uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {creating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Creando...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Crea Torneo</>
                  )}
                </button>
              </div>
            )}

            {/* JOIN TAB */}
            {activeTab === 'join' && (
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-white/70 uppercase tracking-wider">Codice di Invito</label>
                  <div className="relative">
                    <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <input
                      type="text"
                      value={joinCode}
                      onChange={e => setJoinCode(e.target.value.toUpperCase().slice(0, 8))}
                      placeholder="ABCD12"
                      className="w-full pl-11 pr-4 py-3 bg-black/40 border border-white/10 hover:border-white/20 focus:border-yellow-500/50 rounded-xl text-sm text-white font-mono placeholder-white/20 focus:outline-none transition-all uppercase"
                      maxLength={8}
                    />
                  </div>
                </div>
                {error && <p className="text-red-400 text-xs">{error}</p>}
                {success && <p className="text-green-400 text-xs">{success}</p>}
                <button
                  onClick={() => { playSfx('click'); triggerHaptic('medium'); handleJoin(); }}
                  disabled={joining}
                  className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:bg-white/10 text-black font-black text-sm uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  {joining ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Unisciti...</>
                  ) : (
                    <><ChevronRight className="w-4 h-4" /> Unisciti al Torneo</>
                  )}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}